import {
  Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit,
  PLATFORM_ID, SimpleChanges, ViewChild, inject, signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';

interface FollowerUser {
  _id: string;
  displayName: string;
  userName: string;
  profileImage: { urlMedium: string } | null;
}

interface FollowListResponse {
  items: FollowerUser[];
  total: number;
  page: number;
  limit: number;
}

const PAGE_SIZE = 24;

@Component({
  selector: 'app-followers-following',
  imports: [CommonModule, RouterLink],
  templateUrl: './followers-following.html',
})
export class FollowersFollowing implements OnInit, OnChanges, OnDestroy {
  /** 'follower' | 'following' — passed from parent based on active tab */
  @Input() type: 'follower' | 'following' = 'follower';

  /** Profile owner's userName from the parent */
  @Input() userName: string = '';

  /** Bottom-of-list marker — scrolling it into view loads the next page. */
  @ViewChild('sentinel') set sentinelRef(ref: ElementRef<HTMLElement> | undefined) {
    this.observeSentinel(ref?.nativeElement);
  }

  private http       = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private zone       = inject(NgZone);

  users     = signal<FollowerUser[]>([]);
  total     = signal(0);
  isLoading = signal(true);
  allLoaded = signal(false);
  error     = signal<string | null>(null);

  private page = 1;
  private request?: Subscription;
  private observer?: IntersectionObserver;

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) this.resetAndLoad();
  }

  /** Going straight from one profile's Followers to another's reuses this
   *  component with a new userName — it used to keep showing the old list. */
  ngOnChanges(changes: SimpleChanges): void {
    const changed = (changes['userName'] && !changes['userName'].firstChange)
      || (changes['type'] && !changes['type'].firstChange);
    if (changed && isPlatformBrowser(this.platformId)) this.resetAndLoad();
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.observer?.disconnect();
  }

  /** Single source of truth for the ui-avatars fallback used across this page. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  private resetAndLoad(): void {
    // Drop any in-flight page for the previous person so it can't land in
    // (and overwrite) the new list.
    this.request?.unsubscribe();
    this.users.set([]);
    this.total.set(0);
    this.page = 1;
    this.allLoaded.set(false);
    this.isLoading.set(false);
    this.error.set(null);
    this.loadNextPage();
  }

  /** Fetches the next page and appends it — previously only page 1 was ever requested. */
  loadNextPage(): void {
    if (!this.userName || this.isLoading() || this.allLoaded()) return;
    this.isLoading.set(true);
    this.error.set(null);

    const endpoint = this.type === 'follower'
      ? `${environment.apiUrl}/follows/${this.userName}/followers`
      : `${environment.apiUrl}/follows/${this.userName}/following`;
    const params = new HttpParams().set('page', this.page).set('limit', PAGE_SIZE);

    this.request = this.http.get<{ data: FollowListResponse }>(endpoint, { params }).subscribe({
      next: res => {
        const newItems = res.data?.items ?? [];
        const seen = new Set(this.users().map(u => u._id));
        this.users.set([...this.users(), ...newItems.filter(u => !seen.has(u._id))]);
        this.total.set(res.data?.total ?? this.users().length);
        this.page += 1;
        this.allLoaded.set(newItems.length < PAGE_SIZE || this.users().length >= this.total());
        this.isLoading.set(false);
      },
      error: () => {
        this.error.set('Failed to load list. Please try again.');
        this.isLoading.set(false);
      },
    });
  }

  retry(): void {
    if (this.users().length) this.loadNextPage();
    else this.resetAndLoad();
  }

  private observeSentinel(el?: HTMLElement): void {
    this.observer?.disconnect();
    if (!el || !isPlatformBrowser(this.platformId) || typeof IntersectionObserver === 'undefined') return;

    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) this.zone.run(() => this.loadNextPage());
      }, { rootMargin: '400px 0px' });
      this.observer.observe(el);
    });
  }
}
